import type { Route } from 'fitify-ui-onboarding/src/types/routeTypes'

import { CHF01_ROUTES } from 'data/routes/chf01'
import { CHM01_ROUTES } from 'data/routes/chm01'
import { F01_ROUTES } from 'data/routes/f01'
import { GYMF01_ROUTES } from 'data/routes/gymf01'
import { GYMM01_ROUTES } from 'data/routes/gymm01'
import { HOPIF01_ROUTES } from 'data/routes/hopif01'
import { M01_ROUTES } from 'data/routes/m01'
import { MILICAF01_ROUTES } from 'data/routes/milicaf01'
import { MILICAM01_ROUTES } from 'data/routes/milicam01'
import { SHARED_ROUTES } from 'data/routes/shared'
import { TCHF01_ROUTES } from 'data/routes/tchf01'
import { TCHM01_ROUTES } from 'data/routes/tchm01'
import { WALLPIF01_ROUTES } from 'data/routes/wallpif01'
import { Funnel } from 'types/state'

import {
  getRequiredFunnelPages,
  ONBOARDING_PATH,
} from '../../utils/funnel-pages'

type AppRoutesDefinition = Record<string, Pick<Route<string>, 'path' | 'id'>>

export const APP_PAGES = {
  home: {
    path: '/',
    id: 'homepage',
  },
  gift: {
    path: '/gift',
    id: 'gift',
  },
  giftSuccess: {
    path: '/gift/success',
    id: 'gift_success',
  },
  login: {
    path: '/auth/login',
    id: 'signin',
  },
  loginEmail: {
    path: '/auth/login/email',
    id: 'signin_email',
  },
  passwordReset: {
    path: '/auth/login/password-reset',
    id: 'login_password_reset',
  },
  signup: {
    path: '/auth/signup',
    id: 'signup',
  },
  signupEmail: {
    path: '/auth/signup/email',
    id: 'signup_email',
  },
  redeem: {
    path: '/redeem',
    id: 'redeem',
  },
  redeemSuccess: {
    path: '/redeem/success',
    id: 'redeem_success',
  },
  isic: {
    path: '/isic',
    id: 'isic',
  },
  404: {
    path: '/404',
    id: 'not_found',
  },
  500: {
    path: '/500',
    id: 'server_error',
  },
  ...getRequiredFunnelPages(ONBOARDING_PATH),
} as const satisfies AppRoutesDefinition

export const APP_PAGES_MAP = Object.values(APP_PAGES)

export const FUNNEL_PAGES = {
  [Funnel.CHF01]: CHF01_ROUTES.pages,
  [Funnel.CHM01]: CHM01_ROUTES.pages,
  [Funnel.F01]: F01_ROUTES.pages,
  [Funnel.GYMF01]: GYMF01_ROUTES.pages,
  [Funnel.GYMM01]: GYMM01_ROUTES.pages,
  [Funnel.HOPIF01]: HOPIF01_ROUTES.pages,
  [Funnel.M01]: M01_ROUTES.pages,
  [Funnel.MILICAF01]: MILICAF01_ROUTES.pages,
  [Funnel.MILICAM01]: MILICAM01_ROUTES.pages,
  [Funnel.TCHF01]: TCHF01_ROUTES.pages,
  [Funnel.TCHM01]: TCHM01_ROUTES.pages,
  [Funnel.WALLPIF01]: WALLPIF01_ROUTES.pages,
} as const satisfies Record<Funnel, unknown>

export const FUNNEL_PAGES_MAP: Record<Funnel, Route<string>[]> = {
  [Funnel.CHF01]: CHF01_ROUTES.pagesMap,
  [Funnel.CHM01]: CHM01_ROUTES.pagesMap,
  [Funnel.F01]: F01_ROUTES.pagesMap,
  [Funnel.GYMF01]: GYMF01_ROUTES.pagesMap,
  [Funnel.GYMM01]: GYMM01_ROUTES.pagesMap,
  [Funnel.HOPIF01]: HOPIF01_ROUTES.pagesMap,
  [Funnel.M01]: M01_ROUTES.pagesMap,
  [Funnel.MILICAF01]: MILICAF01_ROUTES.pagesMap,
  [Funnel.MILICAM01]: MILICAM01_ROUTES.pagesMap,
  [Funnel.TCHF01]: TCHF01_ROUTES.pagesMap,
  [Funnel.TCHM01]: TCHM01_ROUTES.pagesMap,
  [Funnel.WALLPIF01]: WALLPIF01_ROUTES.pagesMap,
}

export const ALL_PAGES = {
  app: APP_PAGES,
  ...FUNNEL_PAGES,
} as const satisfies Record<Funnel | 'app', unknown>

export const ALL_PAGES_MAP = [
  ...APP_PAGES_MAP,
  ...CHF01_ROUTES.pagesMap,
  ...CHM01_ROUTES.pagesMap,
  ...F01_ROUTES.pagesMap,
  ...GYMF01_ROUTES.pagesMap,
  ...GYMM01_ROUTES.pagesMap,
  ...HOPIF01_ROUTES.pagesMap,
  ...M01_ROUTES.pagesMap,
  ...MILICAF01_ROUTES.pagesMap,
  ...MILICAM01_ROUTES.pagesMap,
  ...SHARED_ROUTES.pagesMap,
  ...TCHF01_ROUTES.pagesMap,
  ...TCHM01_ROUTES.pagesMap,
  ...WALLPIF01_ROUTES.pagesMap,
]

export const DETECT_LOCATION_PAGES = [
  ...CHF01_ROUTES.detectLocationPagesMap,
  ...CHM01_ROUTES.detectLocationPagesMap,
  ...F01_ROUTES.detectLocationPagesMap,
  ...GYMF01_ROUTES.detectLocationPagesMap,
  ...GYMM01_ROUTES.detectLocationPagesMap,
  ...HOPIF01_ROUTES.detectLocationPagesMap,
  ...M01_ROUTES.detectLocationPagesMap,
  ...MILICAF01_ROUTES.detectLocationPagesMap,
  ...MILICAM01_ROUTES.detectLocationPagesMap,
  ...TCHF01_ROUTES.detectLocationPagesMap,
  ...TCHM01_ROUTES.detectLocationPagesMap,
  ...WALLPIF01_ROUTES.detectLocationPagesMap,
  APP_PAGES.subscription,
  APP_PAGES.exclusiveOffer,
]

export const NO_LEAVE_DIALOG_PAGES = [
  ...APP_PAGES_MAP,
  ...CHF01_ROUTES.noLeaveDialogPagesMap,
  ...CHM01_ROUTES.noLeaveDialogPagesMap,
  ...F01_ROUTES.noLeaveDialogPagesMap,
  ...GYMF01_ROUTES.noLeaveDialogPagesMap,
  ...GYMM01_ROUTES.noLeaveDialogPagesMap,
  ...HOPIF01_ROUTES.noLeaveDialogPagesMap,
  ...M01_ROUTES.noLeaveDialogPagesMap,
  ...MILICAF01_ROUTES.noLeaveDialogPagesMap,
  ...MILICAM01_ROUTES.noLeaveDialogPagesMap,
  ...TCHF01_ROUTES.noLeaveDialogPagesMap,
  ...TCHM01_ROUTES.noLeaveDialogPagesMap,
  ...WALLPIF01_ROUTES.noLeaveDialogPagesMap,
]
